import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeftIcon } from './icons/ArrowLeftIcon';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { generateAdImages } from '../services/geminiService';
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from './ui/dialog';

export const GeneratedImagesPage = ({ initialData, onBack, onSaveProject, characters }) => {
    const [images, setImages] = useState(initialData.generatedAdImages || []);
    const [originalImageUrls, setOriginalImageUrls] = useState(initialData.originalImageUrls || []); // URLs for backend save
    const [creditsUsed, setCreditsUsed] = useState(initialData.creditsUsed || 0); // Track credits used
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState('');
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState({ url: '', index: 0 });

    const conceptText = initialData.script || '';
    const aspectRatio = initialData.aspectRatio || '16:9';
    const numImages = initialData.conceptOptions?.numImages || 4;

    const startGeneration = useCallback(async () => {
        if (images.length > 0 || !conceptText.trim()) return;

        setIsGenerating(true);
        setError('');
        setProgress('Preparing image generation...');

        const allChars = characters || initialData.characters || [];
        const referenceImages = allChars
            .filter(c => c.image)
            .map(c => ({ name: c.name, image: c.image, type: c.category || 'character', description: c.description || '' }));

        allChars.forEach(c => {
            if (c.costumeAndAccessoryImages) {
                c.costumeAndAccessoryImages.filter(Boolean).forEach(img => {
                    referenceImages.push({ name: `${c.name} - Costume`, image: img, type: 'costume', description: `Costume for ${c.name}` });
                });
            }
        });

        const directions = [
            'Wide establishing shot showcasing the full scene. Natural, ambient lighting with depth of field.',
            'Close-up product shot highlighting details, textures, and branding. Dramatic lighting.',
            'Medium shot capturing human interaction with the product. Warm, lifestyle feel.',
            'Creative artistic angle — top-down flat lay or low-angle perspective. Bold colors.',
        ];
        const imageVariations = Array.from({ length: numImages }, (_, i) => ({
            index: i + 1,
            direction: directions[i % directions.length]
        }));

        try {
            const result = await generateAdImages(
                { conceptText, numImages, aspectRatio, referenceImages, imageVariations },
                (p) => setProgress(p?.message || 'Generating...')
            );
            if (result.success && result.images.length > 0) {
                setImages(result.images); // Base64 or URLs for display
                // Store original URLs for backend save (don't upload base64 to backend)
                if (result.originalUrls && result.originalUrls.length > 0) {
                    setOriginalImageUrls(result.originalUrls);
                    console.log('📎 Stored original URLs for save:', result.originalUrls.length);
                } else {
                    // If no originalUrls, use the images (they might already be URLs)
                    setOriginalImageUrls(result.images.filter(img => img.startsWith('http')));
                }
                // Track credits used from the generation result
                if (result.creditsUsed) {
                    setCreditsUsed(prev => prev + result.creditsUsed);
                    console.log('💰 Credits used for generation:', result.creditsUsed);
                }
                setProgress('');
                setError('');
            } else {
                setError('Generation completed but no images were returned. Please try again.');
                setProgress('');
            }
        } catch (err) {
            console.error('Ad image generation failed:', err);
            const errorMsg = err.message || 'Unknown error occurred';
            // Check for CORS or network errors
            if (errorMsg.includes('CORS') || errorMsg.includes('Network error') || errorMsg.includes('Failed to fetch')) {
                setError('Network error: Unable to connect to the image generation service. This may be a CORS issue - please contact support.');
            } else {
                setError(`Generation failed: ${errorMsg}`);
            }
            setProgress('');
        } finally {
            setIsGenerating(false);
        }
    }, [conceptText, numImages, aspectRatio, characters, initialData.characters, images.length]);

    useEffect(() => {
        if (images.length === 0 && conceptText.trim()) {
            startGeneration();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = () => {
        if (onSaveProject) {
            // Use original URLs for backend (not base64) to avoid upload size issues
            const urlsForSave = originalImageUrls.length > 0 ? originalImageUrls : 
                images.filter(img => img.startsWith('http'));
            
            console.log('💾 GeneratedImagesPage saving:', {
                display_images_count: images.length,
                urls_for_save_count: urlsForSave.length,
                creditsUsed: creditsUsed,
                urls_preview: urlsForSave.map(img => img?.substring(0, 50) + '...')
            });
            
            // Save both field names for compatibility
            // generatedImages: URLs for backend
            // generatedAdImages: display images (may be base64)
            // creditsUsed: total credits used for this project
            onSaveProject({ 
                generatedAdImages: images,      // For display
                generatedImages: urlsForSave,   // URLs for backend save
                originalImageUrls: urlsForSave, // Also store for future saves
                creditsUsed: creditsUsed        // Credits used for generation
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        }
    };

    const handleDownload = async (url, index) => {
        try {
            const resp = await fetch(url);
            const blob = await resp.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `ad-image-${index + 1}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(a.href);
        } catch {
            window.open(url, '_blank');
        }
    };

    const slots = Array.from({ length: numImages }, (_, i) => images[i] || null);

    const handleViewImage = (url, index) => {
        setSelectedImage({ url, index });
        setViewModalOpen(true);
    };

    return (
        <div className="max-w-5xl mx-auto px-4 py-8" data-testid="generated-images-page">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-white" data-testid="generated-images-title">Generated Ad Images</h2>
                    <p className="text-gray-400 text-sm mt-1">
                        {isGenerating ? progress : images.length > 0 ? `${images.length} image${images.length !== 1 ? 's' : ''} generated` : 'No images yet'}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Always show Generate/Retry button when no images */}
                    {!isGenerating && images.length === 0 && (
                        <button
                            onClick={() => { setError(''); startGeneration(); }}
                            className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold text-sm transition-colors flex items-center gap-2"
                            data-testid="generate-images-btn"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {error ? 'Retry Generation' : 'Generate Images'}
                        </button>
                    )}
                    {images.length > 0 && (
                        <button
                            onClick={handleSave}
                            disabled={saved}
                            className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center gap-2 ${
                                saved
                                    ? 'bg-green-600/20 text-green-400 border border-green-500/40'
                                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                            }`}
                            data-testid="save-project-btn"
                        >
                            {saved ? (
                                <>
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                    Saved
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                                    Save Project
                                </>
                            )}
                        </button>
                    )}
                    <button
                        onClick={onBack}
                        className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold text-sm transition-colors flex items-center gap-2"
                        data-testid="back-to-characters-btn"
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                        Back to Characters
                    </button>
                </div>
            </div>

            {/* Error Banner */}
            {error && (
                <div className="mb-6 p-4 bg-red-900/30 border border-red-700/50 rounded-lg" data-testid="error-banner">
                    <div className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                            <p className="text-red-300 text-sm font-medium">Image Generation Failed</p>
                            <p className="text-red-400/80 text-xs mt-1">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {slots.map((img, i) => (
                    <div key={i} className="bg-[#181820] border border-gray-800 rounded-xl overflow-hidden" data-testid={`ad-image-card-${i}`}>
                        {/* Image area */}
                        <div className="aspect-video bg-gray-900 flex items-center justify-center relative">
                            {img ? (
                                <img src={img} alt={`Ad image ${i + 1}`} className="w-full h-full object-cover" />
                            ) : isGenerating ? (
                                <div className="flex flex-col items-center gap-3 text-gray-500">
                                    <LoadingSpinner className="w-10 h-10 animate-spin" />
                                    <span className="text-sm">Generating image {i + 1}...</span>
                                </div>
                            ) : error ? (
                                <div className="flex flex-col items-center gap-2 text-red-400/70">
                                    <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                                    </svg>
                                    <span className="text-xs">Failed</span>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2 text-gray-600">
                                    <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                                    </svg>
                                    <span className="text-xs">Pending</span>
                                </div>
                            )}
                        </div>

                        {/* Action bar */}
                        {img && (
                            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-800">
                                <span className="text-gray-400 text-sm font-medium">Image {i + 1}</span>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handleViewImage(img, i)}
                                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-gray-700 hover:bg-gray-600 text-white transition-colors flex items-center gap-1.5"
                                        data-testid={`view-image-${i}`}
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                        View
                                    </button>
                                    <button
                                        onClick={() => handleDownload(img, i)}
                                        className="px-3 py-1.5 text-xs font-medium rounded-md bg-purple-600 hover:bg-purple-700 text-white transition-colors flex items-center gap-1.5"
                                        data-testid={`download-image-${i}`}
                                    >
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                        Download
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Regenerate button */}
            {!isGenerating && images.length > 0 && (
                <div className="mt-6 flex justify-center">
                    <button
                        onClick={() => { setImages([]); setTimeout(startGeneration, 100); }}
                        className="px-6 py-2.5 border border-gray-700 hover:border-purple-500 bg-gray-800/50 hover:bg-purple-600/10 text-gray-300 hover:text-purple-300 rounded-lg text-sm font-medium transition-all"
                        data-testid="regenerate-images-btn"
                    >
                        Regenerate All Images
                    </button>
                </div>
            )}

            {/* Image View Modal */}
            <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
                <DialogContent className="max-w-4xl w-[90vw] bg-[#1a1a24] border-gray-700 p-0 overflow-hidden" data-testid="view-image-modal">
                    <DialogTitle className="sr-only">View Image {selectedImage.index + 1}</DialogTitle>
                    <div className="relative">
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-[#141418]">
                            <span className="text-white font-medium">Image {selectedImage.index + 1}</span>
                            <button
                                onClick={() => handleDownload(selectedImage.url, selectedImage.index)}
                                className="px-3 py-1.5 text-xs font-medium rounded-md bg-purple-600 hover:bg-purple-700 text-white transition-colors flex items-center gap-1.5"
                                data-testid="modal-download-btn"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                Download
                            </button>
                        </div>
                        {/* Image */}
                        <div className="flex items-center justify-center bg-black/50 max-h-[75vh] overflow-auto">
                            {selectedImage.url && (
                                <img 
                                    src={selectedImage.url} 
                                    alt={`Ad image ${selectedImage.index + 1}`} 
                                    className="max-w-full max-h-[75vh] object-contain"
                                    data-testid="modal-image"
                                />
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};
